import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  DataTable,
  FormField,
  Input,
  PageHeader,
  SearchInput,
  SelectField,
} from "@jod/ui";
import { createFileRoute, Link, Outlet, useRouterState } from "@tanstack/react-router";
import type { FormEvent } from "react";
import { useState } from "react";
import {
  useCreateVenue,
  usePlatformVenues,
  useSetVenueActive,
} from "../../features/platform/queries.ts";
import type { PlatformVenue, VenueStatus, VenueTypeToken } from "../../features/platform/types.ts";

export const Route = createFileRoute("/_app/venues")({ component: VenuesScreen });

const STATUS_OPTIONS = [
  { value: "", label: "All statuses" },
  { value: "active", label: "Active" },
  { value: "suspended", label: "Suspended" },
];

const VENUE_TYPE_OPTIONS: { value: VenueTypeToken; label: string }[] = [
  { value: "bar", label: "Bar" },
  { value: "pub", label: "Pub" },
  { value: "restaurant", label: "Restaurant" },
  { value: "live_music", label: "Live music" },
];

function VenuesScreen() {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  // `venues.$venueId` nests under this route; render the child detail there
  // instead of the list (this component has no implicit Outlet otherwise).
  if (pathname.startsWith("/venues/")) return <Outlet />;
  return <VenuesList />;
}

function VenuesList() {
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const {
    data: venues,
    isLoading,
    error,
  } = usePlatformVenues(search || undefined, (status || undefined) as VenueStatus | undefined);
  const setActive = useSetVenueActive();

  return (
    <div className="space-y-6">
      <PageHeader
        title="Venues"
        subtitle="All tenants on the platform — onboard, suspend, reactivate (FR-SADMIN-010..012)."
      />
      <div className="flex flex-wrap items-center gap-3">
        <SearchInput
          value={search}
          onChange={setSearch}
          placeholder="Search by name (EN/TH)…"
          label="Search venues"
        />
        <select
          aria-label="Filter by status"
          value={status}
          onChange={(event) => setStatus(event.target.value)}
          className="flex h-11 rounded-md border border-input bg-transparent px-3 text-sm"
        >
          {STATUS_OPTIONS.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </div>
      {error ? (
        <p role="alert" className="rounded-md bg-destructive/10 p-3 text-destructive text-sm">
          {error.message}
        </p>
      ) : null}
      <DataTable<PlatformVenue>
        rows={venues ?? []}
        getRowKey={(row) => row.id}
        empty={
          <p className="text-muted-foreground text-sm">
            {isLoading ? "Loading…" : "No venues match."}
          </p>
        }
        columns={[
          {
            key: "name",
            header: "Venue",
            cell: (row) => (
              <Link
                to="/venues/$venueId"
                params={{ venueId: row.id }}
                className="font-medium text-primary"
              >
                {row.name}
                {row.nameTh ? (
                  <span className="block text-muted-foreground text-xs">{row.nameTh}</span>
                ) : null}
              </Link>
            ),
          },
          { key: "type", header: "Type", cell: (row) => row.venueType },
          {
            key: "status",
            header: "Status",
            cell: (row) => (
              <Badge variant={row.status === "active" ? "default" : "destructive"}>
                {row.status}
              </Badge>
            ),
          },
          { key: "branches", header: "Branches", cell: (row) => row.branchCount },
          { key: "staff", header: "Staff", cell: (row) => row.staffCount },
          { key: "sessions", header: "Open sessions", cell: (row) => row.openSessionCount },
          {
            key: "actions",
            header: "",
            cell: (row) => (
              <Button
                variant={row.status === "active" ? "outline" : "default"}
                size="sm"
                disabled={setActive.isPending}
                onClick={() =>
                  setActive.mutate({ venueId: row.id, active: row.status !== "active" })
                }
              >
                {row.status === "active" ? "Suspend" : "Reactivate"}
              </Button>
            ),
          },
        ]}
      />
      <CreateVenueCard />
    </div>
  );
}

function CreateVenueCard() {
  const createVenue = useCreateVenue();
  const [name, setName] = useState("");
  const [nameTh, setNameTh] = useState("");
  const [venueType, setVenueType] = useState<VenueTypeToken>("bar");
  const [address, setAddress] = useState("");
  const [branchName, setBranchName] = useState("");
  const [message, setMessage] = useState<string | null>(null);

  function handleSubmit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    setMessage(null);
    createVenue.mutate(
      {
        name,
        nameTh: nameTh || undefined,
        type: venueType,
        address: address || undefined,
        branchName: branchName || undefined,
      },
      {
        onSuccess: (venue) => {
          setMessage(`Venue "${venue.name}" onboarded with its first branch.`);
          setName("");
          setNameTh("");
          setAddress("");
          setBranchName("");
        },
        onError: (err) => setMessage(err.message),
      },
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="font-display text-lg">Onboard a new venue</CardTitle>
        <p className="text-muted-foreground text-sm">
          Creates the tenant and its first branch. Owner account linking follows owner-admin setup
          (FR-SADMIN-011).
        </p>
      </CardHeader>
      <CardContent>
        <form className="grid gap-4 sm:grid-cols-2" onSubmit={handleSubmit}>
          <FormField id="venue-name" label="Name (EN)">
            <Input
              id="venue-name"
              required
              value={name}
              onChange={(event) => setName(event.target.value)}
            />
          </FormField>
          <FormField id="venue-name-th" label="Name (TH)">
            <Input
              id="venue-name-th"
              value={nameTh}
              onChange={(event) => setNameTh(event.target.value)}
            />
          </FormField>
          <SelectField
            id="venue-type"
            label="Venue type"
            value={venueType}
            onChange={(value) => setVenueType(value as VenueTypeToken)}
            options={VENUE_TYPE_OPTIONS}
          />
          <FormField id="venue-address" label="Address">
            <Input
              id="venue-address"
              value={address}
              onChange={(event) => setAddress(event.target.value)}
            />
          </FormField>
          <FormField id="branch-name" label="First branch name" hint="Defaults to “Main”.">
            <Input
              id="branch-name"
              value={branchName}
              onChange={(event) => setBranchName(event.target.value)}
            />
          </FormField>
          <div className="flex items-end">
            <Button type="submit" disabled={createVenue.isPending}>
              {createVenue.isPending ? "Creating…" : "Create venue"}
            </Button>
          </div>
          {message ? <p className="text-sm sm:col-span-2">{message}</p> : null}
        </form>
      </CardContent>
    </Card>
  );
}
